import { useState } from 'react';
import { ErroreApi } from '../api/candidatura.js';
import {
  type CandidaturaCompleta,
  type Stato,
  ETICHETTE,
  indirizzoCurriculum,
  indirizzoStampa,
  useAnnota,
  useCambiaStato,
  useCandidatura,
} from '../api/personale.js';

/**
 * Una candidatura, come la legge chi decide se chiamare.
 *
 * Tutto su una pagina, nell'ordine in cui serve: prima chi è e come lo si
 * raggiunge, poi cosa cerca, poi cosa ha fatto. In cima le tre cose che si
 * fanno davvero — scaricare il curriculum, stampare, cambiare stato — e in
 * fondo la storia: chi l'ha guardata, quando, e cosa ha scritto.
 *
 * Nel v2 quest'ultima parte non c'è: l'unico modo di sapere se un collega ha
 * già chiamato una persona è chiederglielo.
 */

const NOMI_SCUOLA: Record<string, string> = {
  highSchool: 'High school',
  college: 'College',
  tradeSchool: 'Business or trade school',
  professionalSchool: 'Professional school',
};

const STATI: Stato[] = ['submitted', 'reviewing', 'interview', 'hired', 'rejected'];

const sino = (v: boolean | null | undefined) => (v === null || v === undefined ? '—' : v ? 'Yes' : 'No');
const testo = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '—');

function giorno(v: string | null | undefined) {
  if (!v) return '—';
  // un giorno semplice («1990-05-10») è un giorno, non un istante: letto come
  // istante è la mezzanotte di Greenwich, che a New York è ancora il 9
  const semplice = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  const d = semplice
    ? new Date(Number(semplice[1]), Number(semplice[2]) - 1, Number(semplice[3]))
    : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US');
}

function quando(v: string | null | undefined) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US');
}

export function Candidatura({ id }: { id: number }) {
  const dati = useCandidatura(id);
  const cambia = useCambiaStato(id);
  const annota = useAnnota(id);
  const [nota, setNota] = useState('');

  if (dati.isPending) return <div className="pagina-hr attesa">One moment…</div>;
  if (dati.error || !dati.data) {
    return (
      <div className="pagina-hr">
        <a className="indietro" href="/staff">
          ← Back to applications
        </a>
        <p>This application does not exist.</p>
      </div>
    );
  }

  const c = dati.data;

  const nome = [c.firstName, c.middleInitial, c.lastName].filter(Boolean).join(' ');
  const telefono = [c.phoneAreaCode, c.phone].filter(Boolean).join(' ');
  const indirizzo = [c.addressStreet, c.city, c.state, c.zip].filter(Boolean).join(', ');

  return (
    <div className="pagina-hr">
      <a className="indietro" href="/staff">
        ← Back to applications
      </a>

      <header className="testa-hr">
        <div>
          <h1>{nome}</h1>
          <p className="tenue">
            Received {giorno(c.submittedAt)} · Application #{c.id}
          </p>
        </div>
        <div className="testa-destra">
          {/* il file registrato ma sparito dal disco si dice qui: prima il
              pulsante c'era, e il clic apriva il JSON grezzo dell'errore */}
          {c.files.length === 0 ? (
            <span className="pastiglia">no resume</span>
          ) : c.files[0]!.inArchive === false ? (
            <span className="tenue" role="note">
              The resume is recorded but the file is missing from the archive.
            </span>
          ) : (
            <a className="btn" href={indirizzoCurriculum(c.id)}>
              Download resume
            </a>
          )}
          <a className="btn" href={indirizzoStampa(c.id)} target="_blank" rel="noreferrer">
            Print
          </a>
        </div>
      </header>

      {/* lo stato: si cambia da qui, e ogni cambio resta scritto con chi l'ha fatto */}
      <div className="stati">
        {STATI.map((s) => (
          <button
            key={s}
            className={c.status === s ? 'stato stato-scelto' : 'stato'}
            disabled={cambia.isPending}
            onClick={() => cambia.mutate({ status: s })}
          >
            {ETICHETTE[s]}
          </button>
        ))}
      </div>

      <div className="colonne">
        <div>
          <Riquadro titolo="Contact">
            <Dato etichetta="E-mail" valore={testo(c.email)} />
            <Dato etichetta="Telephone" valore={testo(telefono)} />
            <Dato etichetta="Address" valore={testo(indirizzo)} />
            <Dato etichetta="18 or older" valore={sino(c.isOver18)} />
            <Dato etichetta="Authorized to work in the U.S." valore={sino(c.isAuthorizedToWork)} />
            <Dato etichetta="Driver license" valore={sino(c.hasDriverLicense)} />
          </Riquadro>

          <Riquadro titolo="The job">
            <Dato etichetta="Position desired" valore={testo(c.positionDesired)} />
            <Dato etichetta="Can start" valore={giorno(c.availableFrom)} />
            <Dato etichetta="Salary desired" valore={testo(c.salaryDesired)} />
            <Dato etichetta="Employed now" valore={sino(c.employedNow)} />
            <Dato etichetta="May contact employer" valore={sino(c.mayContactEmployer)} />
            <Dato
              etichetta="Applied before"
              valore={
                sino(c.appliedBefore) +
                [c.appliedBeforeWhere, c.appliedBeforeWhen].filter(Boolean).map((x) => ` · ${x}`).join('')
              }
            />
            <Dato etichetta="Referred by" valore={testo(c.referredBy)} />
          </Riquadro>

          {c.schools.length > 0 && (
            <Riquadro titolo="Education">
              {c.schools.map((s, i) => (
                <p key={i} className="voce">
                  <strong>{NOMI_SCUOLA[s.kind] ?? s.kind}</strong>
                  <br />
                  <span className="tenue">
                    {[
                      s.name,
                      s.yearsCompleted != null ? `${s.yearsCompleted} yrs` : null,
                      s.graduated === null ? null : s.graduated ? 'graduated' : 'did not graduate',
                      s.subjects,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </span>
                </p>
              ))}
            </Riquadro>
          )}

          {c.employments.length > 0 && (
            <Riquadro titolo="Former employment">
              {c.employments.map((e, i) => (
                <p key={i} className="voce">
                  <strong>{testo(e.employerName)}</strong>
                  <br />
                  <span className="tenue">
                    {[e.jobTitle, `${giorno(e.fromDate)} – ${giorno(e.toDate)}`, e.phone]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {(e.supervisorName || e.startSalary || e.finalSalary || e.address || e.city) && (
                    <>
                      <br />
                      <span className="tenue">
                        {[
                          e.supervisorName ? `Supervisor: ${e.supervisorName}` : null,
                          e.startSalary || e.finalSalary
                            ? `Pay: ${e.startSalary || '—'} to ${e.finalSalary || '—'}`
                            : null,
                          [e.address, e.city, e.state_, e.zip].filter(Boolean).join(', ') || null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </>
                  )}
                  {e.reasonForLeaving && (
                    <>
                      <br />
                      <span className="tenue">Left because: {e.reasonForLeaving}</span>
                    </>
                  )}
                  {e.dutiesAndSkills && (
                    <>
                      <br />
                      <span>{e.dutiesAndSkills}</span>
                    </>
                  )}
                </p>
              ))}
            </Riquadro>
          )}

          {c.references.length > 0 && (
            <Riquadro titolo="References">
              {c.references.map((r, i) => (
                <p key={i} className="voce">
                  {[r.name, r.role, r.company, r.address, r.addressExtra, r.phone].filter(Boolean).join(' · ') || '—'}
                </p>
              ))}
            </Riquadro>
          )}

          <Riquadro titolo="General">
            <Dato etichetta="Special skills" valore={testo(c.specialSkills)} />
            <Dato etichetta="Certifications" valore={testo(c.currentCertifications)} />
            <Dato
              etichetta="Military service"
              valore={testo(c.militaryService) + (c.militaryRank ? ` · ${c.militaryRank}` : '')}
            />
            <Dato etichetta="Guard or Reserves" valore={testo(c.nationalGuardReserves)} />
            <Dato etichetta="Convicted of a crime" valore={sino(c.convictedOfCrime)} />
            {c.convictedOfCrime && (
              <Dato etichetta="Explanation" valore={testo(c.crimeExplanation)} />
            )}
            <Dato
              etichetta="Emergency contact"
              valore={
                [c.emergencyName, c.emergencyPhone, c.emergencyAddress].filter(Boolean).join(' · ') ||
                '—'
              }
            />
            <Dato
              etichetta="Screening consent"
              valore={c.screeningConsentAt ? `Given ${giorno(c.screeningConsentAt)}` : 'Not given'}
            />
          </Riquadro>
        </div>

        <div>
          <Riquadro titolo="History">
            <form
              className="nota"
              onSubmit={(e) => {
                e.preventDefault();
                if (!nota.trim()) return;
                annota.mutate(nota, { onSuccess: () => setNota('') });
              }}
            >
              {/* la nota rifiutata — troppo lunga — si dice: prima non
                  succedeva niente, e la nota restava lì senza un perché */}
              {annota.error && (
                <div className="avviso" role="alert">
                  {annota.error instanceof ErroreApi ? annota.error.message : 'The note was not saved.'}
                </div>
              )}
              <textarea
                placeholder="Add a note — called her, no answer"
                value={nota}
                onChange={(e) => {
                  setNota(e.target.value);
                  if (annota.isError) annota.reset();
                }}
              />
              <button className="btn" type="submit" disabled={annota.isPending || !nota.trim()}>
                Add note
              </button>
            </form>

            {c.events.length === 0 && <p className="tenue">Nothing yet.</p>}
            {c.events.map((e) => (
              <div key={e.id} className="evento">
                <div className="evento-quando">{quando(e.createdAt)}</div>
                <div>
                  <strong>{titoloEvento(e.kind)}</strong>
                  {e.staffName && <span className="tenue"> · {e.staffName}</span>}
                  {e.note && <div>{e.note}</div>}
                </div>
              </div>
            ))}
          </Riquadro>
        </div>
      </div>
    </div>
  );
}

function titoloEvento(kind: string): string {
  if (kind === 'submitted') return 'Application received';
  if (kind === 'note') return 'Note';
  if (kind === 'confirmationSent') return 'Confirmation e-mail sent';
  if (kind === 'confirmationNotSent') return 'Confirmation e-mail not sent';
  // le due che nascono dal travaso: una candidatura del 2021 le porta tutte e due
  if (kind === 'imported') return 'Imported from the previous system';
  if (kind === 'archived') return 'Archived in the previous system';
  const stato = kind.replace('status:', '') as Stato;
  return ETICHETTE[stato] ? `Moved to ${ETICHETTE[stato]}` : kind;
}

function Riquadro({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <section className="riquadro">
      <h2>{titolo}</h2>
      {children}
    </section>
  );
}

function Dato({ etichetta, valore }: { etichetta: string; valore: string }) {
  return (
    <div className="dato">
      <span className="dato-etichetta">{etichetta}</span>
      <span className="dato-valore">{valore}</span>
    </div>
  );
}
